import { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Box } from '@mui/material'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, Typography } from '@carto/meridian-ds/components'
import { CheckOutlined } from '@mui/icons-material'
import useTimer from 'features/common/hooks/useTimer'
import { TAKEOVER_TIMEOUT_SEC } from 'features/collaboration/types'
import { CollaborativeResourceType } from 'features/common'

export type TakeOverResponseDialogProps = {
  resourceType: CollaborativeResourceType
  open: boolean
  onAccept: () => void
  onReject: () => void
  isLoading: boolean
  user?: string
  timeout: number
  onTimeoutExpired?: () => void
}

function TakeOverResponseDialog({
  resourceType: _resourceType,
  open,
  onAccept,
  onReject,
  isLoading,
  user,
  timeout,
  onTimeoutExpired
}: TakeOverResponseDialogProps) {
  const intl = useIntl()
  const resourceType = intl.formatMessage({ id: `common.shareDialog.resourceTypes.${_resourceType}` })

  const takeOverAutoAcceptDeadlineRef = useRef(timeout + TAKEOVER_TIMEOUT_SEC * 1000)
  useEffect(() => {
    takeOverAutoAcceptDeadlineRef.current = timeout + TAKEOVER_TIMEOUT_SEC * 1000
  }, [timeout])
  const timeLeft = useTimer(takeOverAutoAcceptDeadlineRef.current)

  // Store the timeout callback in a ref to prevent infinite loops
  // This is necessary because onTimeoutExpired is a prop that might change on every render
  const timeoutCallbackRef = useRef(onTimeoutExpired)

  // Update the ref whenever onTimeoutExpired changes
  // This ensures we always have the latest callback available
  useEffect(() => {
    timeoutCallbackRef.current = onTimeoutExpired
  }, [onTimeoutExpired])

  // Check for timeout expiration
  // Using the ref instead of the prop directly prevents unnecessary re-renders
  // and potential infinite loops
  useEffect(() => {
    if (open && timeLeft !== undefined && timeLeft <= 0) {
      timeoutCallbackRef.current?.()
    }
  }, [open, timeLeft])

  return (
    <Dialog data-testid="takeover-response-dialog" open={open} size="small">
      <DialogHeader
        title={<FormattedMessage id="collaboration.takeOverResponseDialog.title" values={{ resourceType }} />}
      />
      <DialogContent>
        <Typography component="span" variant="body2" color="textSecondary">
          <FormattedMessage
            id="collaboration.takeOverResponseDialog.message1"
            values={{
              user,
              strong: (content) => <strong>{content}</strong>,
              resourceType
            }}
          />
          {typeof timeLeft === 'number' && (
            <Box sx={{ mt: 2 }}>
              <Typography component="span" variant="body2" color="textSecondary">
                <FormattedMessage
                  id="collaboration.takeOverResponseDialog.message2"
                  values={{
                    timer: Math.ceil(timeLeft / 1000),
                    strong: (content) => <strong>{content}</strong>,
                    resourceType
                  }}
                />
              </Typography>
            </Box>
          )}
        </Typography>
      </DialogContent>
      <DialogFooter>
        <Button onClick={onReject} disabled={isLoading} color="error">
          <FormattedMessage id="collaboration.takeOverResponseDialog.deny" />
        </Button>

        <Button
          onClick={onAccept}
          variant="contained"
          color="primary"
          loading={isLoading}
          loadingPosition="start"
          startIcon={<CheckOutlined />}
        >
          <FormattedMessage id="collaboration.takeOverResponseDialog.approve" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default TakeOverResponseDialog
